import { useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { Archive, Ban, CircleCheck, Hourglass, Package, PackageCheck, PackageX, Pause, X } from 'lucide-react';

import { WAREHOUSE_DELIVERY_PLURAL_STATES_LABELS } from './constants/states';
import { ActionBar } from 'components/ActionBar';
import { TrelloView } from 'components/TrelloView/TrelloView';
import { WarehouseDeliveryTrelloItem } from './WarehouseDeliveryTrelloItem';
import {
    WarehouseDeliveryFragmentFragment,
    DeliveryState,
    useWarehouseDeliveryUpdatedSubscription,
    TotadminPermission,
} from 'data/__generated__';
import { warehouseDeliveryUpdatedHandler } from './utils/warehouseDeliveryUpdatedHandler';
import { MarkAsPackagingPopup } from './Popups/MarkAsPackagingPopup';
import { MarkAsReadyPopup } from './Popups/MarkAsReadyPopup';
import { MarkAsDeliveredPopup } from './Popups/MarkAsDeliveredPopup';
import { MarkAsInProgressPopup } from './Popups/MarkAsInProgressPopup';
import { MarkAsPausedPopup } from './Popups/MarkAsPausedPopup';
import { MarkAsNotDeliveredPopup } from './Popups/MarkAsNotDeliveredPopup';
import { MarkAsAbortedPopup } from './Popups/MarkAsAbortedPopup';
import { ArchivePopup } from './Popups/ArchivePopup';

export const WarehouseDeliveryTrelloView = ({
    warehouseDeliveries,
    searchValue,
    selectedWarehouseDeliveryIds,
    setSelectedWarehouseDeliveryIds,
}: {
    warehouseDeliveries: WarehouseDeliveryFragmentFragment[];
    searchValue: string;
    selectedWarehouseDeliveryIds: string[];
    setSelectedWarehouseDeliveryIds: (value: React.SetStateAction<string[]>) => void;
}) => {
    const [isMarkAsPackagingPopupOpen, setIsMarkAsPackagingPopupOpen] = useState<boolean>(false);
    const [isMarkAsReadyPopupOpen, setIsMarkAsReadyPopupOpen] = useState<boolean>(false);
    const [isMarkAsDeliveredPopupOpen, setIsMarkAsDeliveredPopupOpen] = useState<boolean>(false);
    const [isMarkAsInProgressPopupOpen, setIsMarkAsInProgressPopupOpen] = useState<boolean>(false);
    const [isMarkAsPausedPopupOpen, setIsMarkAsPausedPopupOpen] = useState<boolean>(false);
    const [isMarkAsNotDeliveredPopupOpen, setIsMarkAsNotDeliveredPopupOpen] = useState<boolean>(false);
    const [isMarkAsAbortedPopupOpen, setIsMarkAsAbortedPopupOpen] = useState<boolean>(false);
    const [isArchivePopupOpen, setIsArchivePopupOpen] = useState<boolean>(false);

    useWarehouseDeliveryUpdatedSubscription({
        onData({ data: { data } }) {
            if (!data) return;
            const { warehouseDeliveryUpdated } = data;
            warehouseDeliveryUpdatedHandler(warehouseDeliveryUpdated);
            toast.warning("Quelqu'un vient de modifier une ou plusieurs livraison(s), la vue a été mise à jour !");
        },
    });

    const filteredWarehouseDeliveries = warehouseDeliveries.filter(
        (delivery) =>
            !searchValue ||
            delivery._id.toLowerCase().includes(searchValue.toLowerCase()) ||
            delivery.siteId?.toLowerCase().includes(searchValue.toLowerCase()) ||
            delivery.site?.name?.toLowerCase().includes(searchValue.toLowerCase()),
    );

    function toggleWarehouseDeliverySelection(warehouseDeliveryIdToToggle: string) {
        if (selectedWarehouseDeliveryIds.includes(warehouseDeliveryIdToToggle)) {
            setSelectedWarehouseDeliveryIds(
                selectedWarehouseDeliveryIds.filter(
                    (warehouseDeliveryId) => warehouseDeliveryId !== warehouseDeliveryIdToToggle,
                ),
            );
        } else {
            setSelectedWarehouseDeliveryIds([...selectedWarehouseDeliveryIds, warehouseDeliveryIdToToggle]);
        }
    }

    function sortByDeliveryDate(
        deliveryA: WarehouseDeliveryFragmentFragment,
        deliveryB: WarehouseDeliveryFragmentFragment,
    ) {
        if ((deliveryA.deliveryDate && !deliveryB.deliveryDate) || deliveryA.deliveryDate > deliveryB.deliveryDate) {
            return 1;
        }
        if (deliveryA.deliveryDate === deliveryB.deliveryDate) {
            return deliveryA.updatedAt > deliveryB.updatedAt ? 1 : -1;
        }
        return -1;
    }

    function getDeliveriesByState({ state }: { state: DeliveryState }) {
        const sortedWarehouseDeliveries = filteredWarehouseDeliveries
            .filter((delivery) => delivery.state === state)
            .sort(sortByDeliveryDate);
        if ([DeliveryState.Aborted, DeliveryState.Archived, DeliveryState.Delivered].includes(state)) {
            sortedWarehouseDeliveries.reverse();
        }
        return sortedWarehouseDeliveries.map((warehouseDelivery) => {
            return {
                _id: warehouseDelivery._id,
                content: (
                    <WarehouseDeliveryTrelloItem
                        warehouseDelivery={warehouseDelivery}
                        isSelected={selectedWarehouseDeliveryIds.includes(warehouseDelivery._id)}
                        toggleDeliverySelection={toggleWarehouseDeliverySelection}
                    />
                ),
            };
        });
    }

    const actionSections = [
        {
            title: 'Actions',
            actions: [
                {
                    icon: <X />,
                    label: 'Tout désélectionner',
                    onClick: () => {
                        setSelectedWarehouseDeliveryIds([]);
                    },
                    hidden: !selectedWarehouseDeliveryIds.length,
                },
            ],
        },
        {
            title: 'Marquer comme',
            actions: [
                {
                    icon: <Package />,
                    label: 'en préparation',
                    requiredPermissions: [TotadminPermission.WarehouseDeliveriesManage],
                    onClick: () => {
                        setIsMarkAsPackagingPopupOpen(true);
                    },
                    hidden:
                        !selectedWarehouseDeliveryIds.length ||
                        filteredWarehouseDeliveries.some(
                            ({ _id, state }) =>
                                selectedWarehouseDeliveryIds.includes(_id) && state !== DeliveryState.Created,
                        ),
                },
                {
                    icon: <PackageCheck />,
                    label: 'prête(s)',
                    requiredPermissions: [TotadminPermission.WarehouseDeliveriesManage],
                    onClick: () => {
                        setIsMarkAsReadyPopupOpen(true);
                    },
                    hidden:
                        !selectedWarehouseDeliveryIds.length ||
                        filteredWarehouseDeliveries.some(
                            ({ _id, state }) =>
                                selectedWarehouseDeliveryIds.includes(_id) && state !== DeliveryState.Packaging,
                        ),
                },
                {
                    icon: <Hourglass />,
                    label: 'en cours',
                    requiredPermissions: [TotadminPermission.WarehouseDeliveriesManage],
                    onClick: () => {
                        setIsMarkAsInProgressPopupOpen(true);
                    },
                    hidden:
                        !selectedWarehouseDeliveryIds.length ||
                        filteredWarehouseDeliveries.some(
                            ({ _id, state }) =>
                                selectedWarehouseDeliveryIds.includes(_id) && state !== DeliveryState.Ready,
                        ),
                },
                {
                    icon: <Pause />,
                    label: 'en pause',
                    requiredPermissions: [TotadminPermission.WarehouseDeliveriesManage],
                    onClick: () => {
                        setIsMarkAsPausedPopupOpen(true);
                    },
                    hidden:
                        !selectedWarehouseDeliveryIds.length ||
                        filteredWarehouseDeliveries.some(
                            ({ _id, state }) =>
                                selectedWarehouseDeliveryIds.includes(_id) &&
                                ![DeliveryState.Ready, DeliveryState.InProgress].includes(state),
                        ),
                },
                {
                    icon: <CircleCheck />,
                    label: 'livrée(s)',
                    requiredPermissions: [TotadminPermission.WarehouseDeliveriesManage],
                    onClick: () => {
                        setIsMarkAsDeliveredPopupOpen(true);
                    },
                    hidden:
                        !selectedWarehouseDeliveryIds.length ||
                        filteredWarehouseDeliveries.some(
                            ({ _id, state }) =>
                                selectedWarehouseDeliveryIds.includes(_id) &&
                                ![DeliveryState.InProgress, DeliveryState.Paused].includes(state),
                        ),
                },
                {
                    icon: <PackageX />,
                    label: 'non livrée(s)',
                    requiredPermissions: [TotadminPermission.WarehouseDeliveriesManage],
                    onClick: () => {
                        setIsMarkAsNotDeliveredPopupOpen(true);
                    },
                    hidden:
                        !selectedWarehouseDeliveryIds.length ||
                        filteredWarehouseDeliveries.some(
                            ({ _id, state }) =>
                                selectedWarehouseDeliveryIds.includes(_id) &&
                                ![DeliveryState.InProgress, DeliveryState.Paused].includes(state),
                        ),
                },
                {
                    icon: <Ban />,
                    label: 'annulée(s)',
                    requiredPermissions: [TotadminPermission.WarehouseDeliveriesManage],
                    onClick: () => {
                        setIsMarkAsAbortedPopupOpen(true);
                    },
                    hidden:
                        !selectedWarehouseDeliveryIds.length ||
                        filteredWarehouseDeliveries.some(
                            ({ _id, state }) =>
                                selectedWarehouseDeliveryIds.includes(_id) &&
                                ![
                                    DeliveryState.Created,
                                    DeliveryState.Packaging,
                                    DeliveryState.Ready,
                                    DeliveryState.InProgress,
                                    DeliveryState.Paused,
                                ].includes(state),
                        ),
                },
            ],
        },
        {
            title: 'Archivage',
            actions: [
                {
                    icon: <Archive />,
                    label: 'archiver',
                    requiredPermissions: [TotadminPermission.WarehouseDeliveriesManage],
                    onClick: () => {
                        setIsArchivePopupOpen(true);
                    },
                    hidden:
                        !selectedWarehouseDeliveryIds.length ||
                        filteredWarehouseDeliveries.some(
                            ({ _id, state }) =>
                                selectedWarehouseDeliveryIds.includes(_id) &&
                                ![DeliveryState.Delivered, DeliveryState.Aborted].includes(state),
                        ),
                },
            ],
        },
    ];

    const initialData = {
        columns: [
            DeliveryState.Created,
            DeliveryState.Packaging,
            DeliveryState.Paused,
            DeliveryState.Ready,
            DeliveryState.InProgress,
            DeliveryState.Delivered,
            DeliveryState.NotDelivered,
            DeliveryState.Aborted,
            DeliveryState.Archived,
        ].map((state) => ({
            title: WAREHOUSE_DELIVERY_PLURAL_STATES_LABELS[state],
            items: getDeliveriesByState({ state }),
        })),
    };

    return (
        <Content>
            <TrelloViewContainer>
                <TrelloView
                    initialData={initialData}
                    selectedItemIds={selectedWarehouseDeliveryIds}
                    setSelectedItemIds={setSelectedWarehouseDeliveryIds}
                />
            </TrelloViewContainer>
            <ActionBar actionSections={actionSections} isInitiallyOpen={!!selectedWarehouseDeliveryIds.length} />
            <MarkAsPackagingPopup
                warehouseDeliveries={filteredWarehouseDeliveries}
                selectedWarehouseDeliveryIds={selectedWarehouseDeliveryIds}
                setSelectedWarehouseDeliveryIds={setSelectedWarehouseDeliveryIds}
                isOpen={isMarkAsPackagingPopupOpen}
                setIsOpen={setIsMarkAsPackagingPopupOpen}
            />
            <MarkAsReadyPopup
                warehouseDeliveries={filteredWarehouseDeliveries}
                selectedWarehouseDeliveryIds={selectedWarehouseDeliveryIds}
                setSelectedWarehouseDeliveryIds={setSelectedWarehouseDeliveryIds}
                isOpen={isMarkAsReadyPopupOpen}
                setIsOpen={setIsMarkAsReadyPopupOpen}
            />
            <MarkAsDeliveredPopup
                warehouseDeliveries={filteredWarehouseDeliveries}
                selectedWarehouseDeliveryIds={selectedWarehouseDeliveryIds}
                setSelectedWarehouseDeliveryIds={setSelectedWarehouseDeliveryIds}
                isOpen={isMarkAsDeliveredPopupOpen}
                setIsOpen={setIsMarkAsDeliveredPopupOpen}
            />
            <MarkAsInProgressPopup
                warehouseDeliveries={filteredWarehouseDeliveries}
                selectedWarehouseDeliveryIds={selectedWarehouseDeliveryIds}
                setSelectedWarehouseDeliveryIds={setSelectedWarehouseDeliveryIds}
                isOpen={isMarkAsInProgressPopupOpen}
                setIsOpen={setIsMarkAsInProgressPopupOpen}
            />
            <MarkAsPausedPopup
                warehouseDeliveries={filteredWarehouseDeliveries}
                selectedWarehouseDeliveryIds={selectedWarehouseDeliveryIds}
                setSelectedWarehouseDeliveryIds={setSelectedWarehouseDeliveryIds}
                isOpen={isMarkAsPausedPopupOpen}
                setIsOpen={setIsMarkAsPausedPopupOpen}
            />
            <MarkAsNotDeliveredPopup
                warehouseDeliveries={filteredWarehouseDeliveries}
                selectedWarehouseDeliveryIds={selectedWarehouseDeliveryIds}
                setSelectedWarehouseDeliveryIds={setSelectedWarehouseDeliveryIds}
                isOpen={isMarkAsNotDeliveredPopupOpen}
                setIsOpen={setIsMarkAsNotDeliveredPopupOpen}
            />
            <MarkAsAbortedPopup
                warehouseDeliveries={filteredWarehouseDeliveries}
                selectedWarehouseDeliveryIds={selectedWarehouseDeliveryIds}
                setSelectedWarehouseDeliveryIds={setSelectedWarehouseDeliveryIds}
                isOpen={isMarkAsAbortedPopupOpen}
                setIsOpen={setIsMarkAsAbortedPopupOpen}
            />
            <ArchivePopup
                warehouseDeliveries={filteredWarehouseDeliveries}
                selectedWarehouseDeliveryIds={selectedWarehouseDeliveryIds}
                setSelectedWarehouseDeliveryIds={setSelectedWarehouseDeliveryIds}
                isOpen={isArchivePopupOpen}
                setIsOpen={setIsArchivePopupOpen}
            />
        </Content>
    );
};

const TrelloViewContainer = styled.div`
    overflow-x: scroll;
`;

const Content = styled.div`
    position: relative;
    display: flex;
    flex: 1;
    overflow: hidden;
`;
